import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/components/AuthProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AnimatePresence } from "framer-motion";
import { ThemeProvider } from "next-themes";
import { CommandPalette } from "@/components/CommandPalette";
import { SaveIndicator } from "@/components/SaveIndicator";
import { OfflineIndicator } from "@/components/mobile/OfflineIndicator";
import { WhatsAppOnboardingModal } from "@/components/WhatsAppOnboardingModal";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

// Eager loaded (critical path)
import Index from "./pages/Index";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";

// Lazy loaded (code splitting)
const Projects = lazy(() => import("./pages/Projects"));
const ClientesLista = lazy(() => import("./pages/ClientesLista"));
const ClientesNovo = lazy(() => import("./pages/ClientesNovo"));
const Propostas = lazy(() => import("./pages/Propostas"));
const ProjetoIndividual = lazy(() => import("./pages/ProjetoIndividual"));
const ProjetoDetalhes = lazy(() => import("./pages/ProjetoDetalhes"));
const ProjetoEdicao = lazy(() => import("./pages/ProjetoEdicao"));
const Financeiro = lazy(() => import("./pages/Financeiro"));
const Documentos = lazy(() => import("./pages/Documentos"));
const Alertas = lazy(() => import("./pages/Alertas"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const Configuracoes = lazy(() => import("./pages/Configuracoes"));
const AgenteIA = lazy(() => import("./pages/AgenteIA"));
const Equipe = lazy(() => import("./pages/Equipe"));
const Contato = lazy(() => import("./pages/Contato"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TestPage = lazy(() => import("./pages/TestPage"));
const AuthTest = lazy(() => import("./pages/AuthTest"));
const Assinatura = lazy(() => import("./pages/Assinatura"));
const ProjectSchedule = lazy(() => import("./pages/ProjectSchedule"));
const GaleriaObra = lazy(() => import("./pages/GaleriaObra"));
const PortalCliente = lazy(() => import("./pages/PortalCliente"));
const PropostaPublica = lazy(() => import("./pages/PropostaPublica"));
const Integracoes = lazy(() => import("./pages/Integracoes"));
const Indicacoes = lazy(() => import("./pages/Indicacoes"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AIToolsHub = lazy(() => import("./pages/AIToolsHub"));
const Planos = lazy(() => import("./pages/Planos"));

// Landing pages segmentadas para ads
const LandingAds = lazy(() => import("./pages/LandingAds"));
const LandingEngenheiros = lazy(() => import("./pages/LandingEngenheiros"));
const LandingArquitetos = lazy(() => import("./pages/LandingArquitetos"));
const LandingConstrutoras = lazy(() => import("./pages/LandingConstrutoras"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Carregando...</p>
      </div>
    </div>
  );
}

// Configure QueryClient with offline-first settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 3,
      networkMode: 'offlineFirst',
    },
    mutations: {
      networkMode: 'offlineFirst',
      retry: 3,
    },
  },
});

// Create persister for offline storage
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'obrim-query-cache',
});

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<PageLoader />}>
        <Routes location={location} key={location.pathname}>
          {/* Public Routes - Eager loaded */}
          <Route path="/" element={<Index />} />
          <Route path="/register" element={<Register />} />

          {/* Landing Pages segmentadas para ads */}
          <Route path="/lp" element={<LandingAds />} />
          <Route path="/engenheiros" element={<LandingEngenheiros />} />
          <Route path="/arquitetos" element={<LandingArquitetos />} />
          <Route path="/construtoras" element={<LandingConstrutoras />} />

          {/* Public Portal Routes - Lazy loaded */}
          <Route path="/obras/:hash" element={<PortalCliente />} />
          <Route path="/proposta/:hash" element={<PropostaPublica />} />

          {/* Test Routes */}
          <Route path="/test" element={<TestPage />} />
          <Route path="/auth-test" element={<AuthTest />} />
          <Route path="/assinatura" element={<Assinatura />} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
          <Route path="/clientes" element={<ProtectedRoute><ClientesLista /></ProtectedRoute>} />
          <Route path="/clientes/novo" element={<ProtectedRoute><ClientesNovo /></ProtectedRoute>} />
          <Route path="/propostas" element={<ProtectedRoute><Propostas /></ProtectedRoute>} />
          <Route path="/projeto/:id" element={<ProtectedRoute><ProjetoIndividual /></ProtectedRoute>} />
          <Route path="/projetos/:id" element={<ProtectedRoute><ProjetoDetalhes /></ProtectedRoute>} />
          <Route path="/projetos/:id/editar" element={<ProtectedRoute><ProjetoEdicao /></ProtectedRoute>} />
          <Route path="/project-schedule/:id" element={<ProtectedRoute><ProjectSchedule /></ProtectedRoute>} />
          <Route path="/galeria-obra/:id" element={<ProtectedRoute><GaleriaObra /></ProtectedRoute>} />
          <Route path="/financeiro" element={<ProtectedRoute><Financeiro /></ProtectedRoute>} />
          <Route path="/documentos" element={<ProtectedRoute><Documentos /></ProtectedRoute>} />
          <Route path="/alertas" element={<ProtectedRoute><Alertas /></ProtectedRoute>} />
          <Route path="/relatorios" element={<ProtectedRoute><Relatorios /></ProtectedRoute>} />
          <Route path="/configuracoes" element={<ProtectedRoute><Configuracoes /></ProtectedRoute>} />
          <Route path="/agente-ia" element={<ProtectedRoute><AgenteIA /></ProtectedRoute>} />
          <Route path="/equipe" element={<ProtectedRoute><Equipe /></ProtectedRoute>} />
          <Route path="/contato" element={<ProtectedRoute><Contato /></ProtectedRoute>} />
          <Route path="/integracoes" element={<ProtectedRoute><Integracoes /></ProtectedRoute>} />
          <Route path="/indicacoes" element={<ProtectedRoute><Indicacoes /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/ia" element={<ProtectedRoute><AIToolsHub /></ProtectedRoute>} />
          <Route path="/planos" element={<Planos />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}

const App = () => {
  console.log('App component loading...');

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister, maxAge: 1000 * 60 * 60 * 24 }}
      >
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <OfflineIndicator />
          <BrowserRouter>
            <AuthProvider>
              <CommandPalette />
              <SaveIndicator />
              <WhatsAppOnboardingModal />
              <AnimatedRoutes />
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </PersistQueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
